import { ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { environment } from '@environment';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { routes } from './app.routes';
import { appInitializer } from './core/providers/app-initializer.provider';
import { ServiceWorkerModule } from '@angular/service-worker';
import { Angulartics2Module } from 'angulartics2';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideTransloco } from '@jsverse/transloco';
import { translocoConfig } from '@app/transloco.config';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { TranslationDomainService } from '@domain/translation-domain.service';
import { AuthInterceptor, TranslationService, WINDOW } from '@ess-front/shared';
import { DateAdapter, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { CustomDateAdapter } from '@app/core/date/custom-date.adapter';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { CustomDatepickerIntl } from '@app/core/date/custom-date-picker-intl.service';
import { SupportedLanguage } from '@app/utils/constants/ess-date-config';
import * as Sentry from '@sentry/angular';
import { release } from '@release';
import { ErrorsInterceptor } from '@app/core/errors/errors-interceptor/errors.interceptor';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Material from '@primeng/themes/material';

Sentry.init({
  denyUrls: ['localhost'],
  dsn: environment.sentryTravelDns,
  enabled: window.location.hostname !== 'localhost',
  environment: environment.production ? 'production' : 'development',
  ignoreErrors: [' 400 ', ' 401 ', ' 403 '],
  release: release,
  beforeSend: event => (window.location.hostname === 'localhost' ? null : event),
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
    ),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(
      ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
      Angulartics2Module.forRoot(),
      MatSnackBarModule,
      MatNativeDateModule,
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorsInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        logErrors: true,
      }),
    },
    { provide: 'googleTagManagerId', useValue: environment.gtmApiKey },
    { provide: 'env', useValue: environment },
    { provide: MAT_DATE_LOCALE, useValue: SupportedLanguage.En },
    { provide: MatDatepickerIntl, useClass: CustomDatepickerIntl },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: WINDOW, useValue: window },
    provideTransloco(translocoConfig),
    provideTranslocoMessageformat(),
    appInitializer,
    {
      provide: TranslationService,
      useClass: TranslationDomainService,
    },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Material,
        options: { darkModeSelector: false },
      },
    }),
  ],
};
